import React, { useState, useEffect } from 'react';
import { Card, Select, Row, Col, Typography, Table, Tag, Button, Space, Spin, Tabs } from 'antd';
import { FilePdfOutlined, FileExcelOutlined, BarChartOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { yearOptions } from '../utils/exportCsv';

const { Title, Text } = Typography;
const { Option } = Select;

const API_BASE = process.env.REACT_APP_API_URL || '';
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatCategory = (cat) => (cat || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

export default function Reports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => { fetchReport();
    const poll = setInterval(() => fetchReportSilent(), 30000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/pnl', { params: { month, year } });
      setReport(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchReportSilent = async () => {
    try {
      const res = await api.get('/reports/pnl', { params: { month, year } });
      setReport(res.data);
    } catch (err) { /* silent */ }
  };

  const downloadPDF = () => {
    const token = localStorage.getItem('token');
    window.open(`${API_BASE}/api/reports/pnl/pdf?month=${month}&year=${year}&token=${token}`, '_blank');
  };

  const downloadExcel = () => {
    const token = localStorage.getItem('token');
    window.open(`${API_BASE}/api/reports/pnl/excel?month=${month}&year=${year}&token=${token}`, '_blank');
  };

  if (loading) return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col><Title level={3} style={{ margin: 0 }}><BarChartOutlined /> Profit & Loss Report</Title></Col>
        <Col>
          <Space>
            <Select value={month} onChange={setMonth} style={{ width: 120 }}>
              {monthNames.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
            </Select>
            <Select value={year} onChange={setYear} style={{ width: 100 }}>
              {yearOptions.map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
            <Button icon={<FilePdfOutlined />} onClick={downloadPDF} danger>Export PDF</Button>
            <Button icon={<FileExcelOutlined />} onClick={downloadExcel} style={{ color: '#217346', borderColor: '#217346' }}>Export Excel</Button>
          </Space>
        </Col>
      </Row>

      {report && (
        <>
          {/* Overall Summary */}
          <Card style={{ borderRadius: 12, marginBottom: 24 }}>
            <Title level={4}>Overall Summary - {monthNames[month - 1]} {year}</Title>
            <Row gutter={24}>
              <Col span={8}>
                <Card style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                  <Text type="secondary">Total Income</Text>
                  <Title level={3} style={{ color: '#52c41a', margin: 0 }}>₹{report.overall.total_income.toLocaleString('en-IN')}</Title>
                </Card>
              </Col>
              <Col span={8}>
                <Card style={{ background: '#fff2f0', borderColor: '#ffccc7' }}>
                  <Text type="secondary">Total Expenses</Text>
                  <Title level={3} style={{ color: '#ff4d4f', margin: 0 }}>₹{report.overall.total_expense.toLocaleString('en-IN')}</Title>
                </Card>
              </Col>
              <Col span={8}>
                <Card style={{ background: report.overall.profit >= 0 ? '#e6f7ff' : '#fff2f0', borderColor: report.overall.profit >= 0 ? '#91d5ff' : '#ffccc7' }}>
                  <Text type="secondary">Net {report.overall.profit >= 0 ? 'Profit' : 'Loss'}</Text>
                  <Title level={3} style={{ color: report.overall.profit >= 0 ? '#1890ff' : '#ff4d4f', margin: 0 }}>
                    ₹{Math.abs(report.overall.profit).toLocaleString('en-IN')}
                  </Title>
                </Card>
              </Col>
            </Row>
          </Card>

          {/* Company-wise Reports — click a company tab to see only its data */}
          {report.companies && report.companies.length > 0 && (
            <Card style={{ borderRadius: 12, marginBottom: 16 }} bodyStyle={{ paddingTop: 8 }}>
              <Title level={4} style={{ marginTop: 0 }}>Company-wise Report</Title>
              <Tabs
                items={report.companies.map(company => ({
                  key: String(company.company_id),
                  label: (
                    <span>
                      {company.company_name}{' '}
                      <Tag color={company.profit >= 0 ? 'green' : 'red'} style={{ marginLeft: 4 }}>
                        {company.profit >= 0 ? '+' : '-'}₹{Math.abs(company.profit).toLocaleString('en-IN')}
                      </Tag>
                    </span>
                  ),
                  children: (
                    <div>
                      <Row gutter={24} style={{ marginBottom: 16 }}>
                        <Col span={8}>
                          <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                            <Text type="secondary">Income</Text>
                            <Title level={4} style={{ color: '#52c41a', margin: 0 }}>₹{company.total_income.toLocaleString('en-IN')}</Title>
                          </Card>
                        </Col>
                        <Col span={8}>
                          <Card size="small" style={{ background: '#fff2f0', borderColor: '#ffccc7' }}>
                            <Text type="secondary">Expense</Text>
                            <Title level={4} style={{ color: '#ff4d4f', margin: 0 }}>₹{company.total_expense.toLocaleString('en-IN')}</Title>
                          </Card>
                        </Col>
                        <Col span={8}>
                          <Card size="small" style={{ background: company.profit >= 0 ? '#e6f7ff' : '#fff2f0', borderColor: company.profit >= 0 ? '#91d5ff' : '#ffccc7' }}>
                            <Text type="secondary">Net {company.profit >= 0 ? 'Profit' : 'Loss'}</Text>
                            <Title level={4} style={{ color: company.profit >= 0 ? '#1890ff' : '#ff4d4f', margin: 0 }}>₹{Math.abs(company.profit).toLocaleString('en-IN')}</Title>
                          </Card>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                <Col span={12}>
                  <Title level={5} style={{ color: '#52c41a' }}>Income Breakdown</Title>
                  <Table
                    dataSource={company.income_breakdown}
                    columns={[
                      { title: 'Category', dataIndex: 'category', render: formatCategory },
                      { title: 'Amount', dataIndex: 'total', render: (v) => <Text style={{ color: '#52c41a' }}>₹{v.toLocaleString('en-IN')}</Text> }
                    ]}
                    rowKey="category"
                    pagination={false}
                    size="small"
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell><Text strong>Total</Text></Table.Summary.Cell>
                        <Table.Summary.Cell><Text strong style={{ color: '#52c41a' }}>₹{company.total_income.toLocaleString('en-IN')}</Text></Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </Col>
                <Col span={12}>
                  <Title level={5} style={{ color: '#ff4d4f' }}>Expense Breakdown</Title>
                  <Table
                    dataSource={company.expense_breakdown}
                    columns={[
                      { title: 'Category', dataIndex: 'category', render: formatCategory },
                      { title: 'Amount', dataIndex: 'total', render: (v) => <Text style={{ color: '#ff4d4f' }}>₹{v.toLocaleString('en-IN')}</Text> }
                    ]}
                    rowKey="category"
                    pagination={false}
                    size="small"
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell><Text strong>Total</Text></Table.Summary.Cell>
                        <Table.Summary.Cell><Text strong style={{ color: '#ff4d4f' }}>₹{company.total_expense.toLocaleString('en-IN')}</Text></Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </Col>
              </Row>
                    </div>
                  )
                }))}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
